import type { TransactionStatement } from '../types';
import {
  formatMoney,
  normalizeDocumentLocale,
  printLabelsFor,
} from '../printLabels';
import { DocumentHeader } from './DocumentHeader';
import { OrderFormFieldValuesTable } from './OrderFormFieldValuesTable';

interface TransactionStatementDocumentProps {
  doc: TransactionStatement;
}

/**
 * 거래명세서(§5).
 * 합계 표시 / 코드=상품코드 / 합계(total) = itemsSubtotal − discountAmount + shippingFee.
 * 마일리지·예치금은 합계 미포함(응답에 필드 자체 없음). 사업자·추가정보 미표시.
 *
 * [글로벌몰] 라벨·금액은 **주문 발생 몰 locale**(doc.localeCode) 기준 — 관리자 UI 언어가 아니라
 * 주문의 몰 기준(일본몰=일본어/JPY). 한국몰(ko-KR/KRW)은 종전과 동일(회귀 0).
 */
export const TransactionStatementDocument = ({
  doc,
}: TransactionStatementDocumentProps) => {
  const locale = normalizeDocumentLocale(doc.localeCode);
  const labels = printLabelsFor(locale);
  const money = (m: Parameters<typeof formatMoney>[0]) => formatMoney(m, locale);
  return (
    <article className="print-surface">
      <DocumentHeader
        title={labels.kind['transaction-statement']}
        header={{
          shopName: labels.shopName,
          orderNo: doc.orderNo,
          issuedAt: doc.orderedAt,
        }}
        localeCode={locale}
      />

      {doc.ordererName ? (
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {labels.field.ordererName}: {doc.ordererName}
        </p>
      ) : null}

      <table>
        <thead>
          <tr>
            <th>{labels.col.code}</th>
            <th>{labels.col.productName}</th>
            <th>{labels.col.option}</th>
            <th className="pd-num">{labels.col.qty}</th>
            <th className="pd-num">{labels.col.unitPrice}</th>
            <th className="pd-num">{labels.col.lineAmount}</th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((item, idx) => (
            <tr key={`${item.productCode}-${idx}`}>
              {/* 항목4 — 코드=자체상품코드(be-order 가 UUID→실 productCode 로 정정).
                  legacy 주문(V44 이전/미보강)은 productCode 가 빈 문자열로 오므로(UUID 비노출
                  규칙) 다른 셀과 동일하게 '-' 로 표기한다. */}
              <td>{item.productCode || '-'}</td>
              <td>{item.productName}</td>
              <td>{item.optionLabel ?? '-'}</td>
              <td className="pd-num">{item.quantity}</td>
              <td className="pd-num">{money(item.unitPrice)}</td>
              <td className="pd-num">{money(item.itemTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 합계 — 배송비(+) · 할인(−) 만 반영. 마일리지·예치금 제외(§5). */}
      <table style={{ marginTop: 12, width: '50%', marginLeft: 'auto' }}>
        <tbody>
          <tr>
            <th>{labels.summary.subtotal}</th>
            <td className="pd-num">{money(doc.summary.itemsSubtotal)}</td>
          </tr>
          <tr>
            <th>{labels.summary.discount}</th>
            <td className="pd-num">- {money(doc.summary.discountAmount)}</td>
          </tr>
          <tr>
            <th>{labels.summary.shippingFee}</th>
            <td className="pd-num">{money(doc.summary.shippingFee)}</td>
          </tr>
          <tr>
            <th style={{ fontSize: 14 }}>{labels.summary.total}</th>
            <td className="pd-num" style={{ fontSize: 14, fontWeight: 700 }}>
              {money(doc.summary.total)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* [233] 주문서 추가정보(통관 등) — 값이 있을 때만 표시. 배송 필수 정보라 문서에 동봉. */}
      <OrderFormFieldValuesTable values={doc.orderFormFieldValues} locale={locale} />

      <p style={{ marginTop: 10, fontSize: 11, color: '#666' }}>
        {labels.transactionNote}
      </p>
    </article>
  );
};
